import React from 'react';

import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import LinkIcon from '@mui/icons-material/Link';

import { colors } from 'theme/colors';

import TasksIcon from 'components/Icons/TasksIcon';

import { QuestionActivityStats } from 'utils/api/modules/entities/entities.types';

import { AuditQuestionDrawerTab } from './AuditQuestionDrawer';

/** The drawer is addressed by `?questionId=<key>&tab=<n>`; both hosts read and write these. */
export const QUESTION_PARAM = 'questionId';
export const TAB_PARAM = 'tab';

const DRAWER_TABS = new Set([
  AuditQuestionDrawerTab.Tasks,
  AuditQuestionDrawerTab.Activity,
  AuditQuestionDrawerTab.LinkedItems,
]);

/** A malformed `?tab=` would otherwise leave MUI with NaN, selecting no tab and rendering blank. */
export const drawerTabFromParam = (value: string | null): AuditQuestionDrawerTab => {
  const requested = Number(value);
  return DRAWER_TABS.has(requested as AuditQuestionDrawerTab)
    ? (requested as AuditQuestionDrawerTab)
    : AuditQuestionDrawerTab.Activity;
};

/** Shared by both hosts. 520px fixed: it holds the task card's three-column meta row. */
export const QUESTION_DRAWER_PAPER_PROPS = {
  sx: { width: 520, borderLeft: `1px solid ${colors.grey[300]}` },
};

/** Accessible name for a row button; the count is additive, so an empty question keeps the label. */
export const rowButtonLabel = (label: string, count: number): string =>
  count === 0 ? label : `${label}, ${count} item${count === 1 ? '' : 's'}`;

export type QuestionRowButtonKind = 'tasks' | 'activity' | 'links';

export type QuestionRowButton = {
  kind: QuestionRowButtonKind;
  label: string;
  tab: AuditQuestionDrawerTab;
  Icon: React.ComponentType;
  count: (stats: QuestionActivityStats, answerRecords: number) => number;
};

export const openQuestionParams = (
  current: URLSearchParams,
  questionKey: string,
  tab: AuditQuestionDrawerTab
): URLSearchParams => {
  const next = new URLSearchParams(current);
  next.set(QUESTION_PARAM, questionKey);
  next.set(TAB_PARAM, String(tab));
  return next;
};

export const closeQuestionParams = (current: URLSearchParams): URLSearchParams => {
  const next = new URLSearchParams(current);
  next.delete(QUESTION_PARAM);
  next.delete(TAB_PARAM);
  return next;
};

export const ROW_BUTTONS: QuestionRowButton[] = [
  { kind: 'tasks', label: 'Tasks', tab: AuditQuestionDrawerTab.Tasks, Icon: TasksIcon, count: (stats) => stats.tasks },
  {
    kind: 'activity',
    label: 'Activity feed',
    tab: AuditQuestionDrawerTab.Activity,
    Icon: FormatListBulletedIcon,
    // the feed merges comments and files, so a file-only question would otherwise badge as empty
    count: (stats) => stats.comments + stats.files,
  },
  {
    kind: 'links',
    label: 'Related records',
    tab: AuditQuestionDrawerTab.LinkedItems,
    Icon: LinkIcon,
    // the tab lists the lookup answer's records beside the manual links, which are all `stats` counts
    count: (stats, answerRecords) => stats.links + answerRecords,
  },
];
